import React, { useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  CircularProgress,
  Skeleton,
  Toolbar,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Alert,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Sort as SortIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { log } from '../../utils/logger';

export interface ColumnDefinition<T = any> {
  key: string;
  label: string;
  sortKey?: string;
  sortable?: boolean;
  filterable?: boolean;
  searchable?: boolean;
  align?: 'left' | 'center' | 'right';
  render?: (item: T, index: number) => React.ReactNode;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  sticky?: boolean;
  headerRender?: () => React.ReactNode;
  cellProps?: (item: T) => object;
  headerProps?: object;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface FilterConfig {
  [key: string]: any;
}

export interface DataTableProps<T = any> {
  // Core data
  data: T[];
  columns: ColumnDefinition<T>[];
  loading?: boolean;
  error?: string | null;
  
  // Sorting
  sortConfig?: SortConfig;
  onSort?: (key: string) => void;
  defaultSort?: SortConfig;
  
  // Selection
  selectable?: boolean;
  selectedItems?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  getItemId?: (item: T) => string;
  
  // Row interaction
  onRowClick?: (item: T, index: number) => void;
  onRowDoubleClick?: (item: T, index: number) => void;
  rowProps?: (item: T, index: number) => object;
  
  // Pagination
  page?: number;
  rowsPerPage?: number;
  totalCount?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions?: number[];
  showPagination?: boolean;
  
  // Search and filtering
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterConfig;
  onFilterChange?: (filters: FilterConfig) => void;
  
  // Mobile support
  renderMobileCard?: (item: T, index: number) => React.ReactNode;
  mobileSortOptions?: { key: string; label: string }[];
  mobileBreakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  // Empty state
  emptyStateComponent?: React.ReactNode;
  emptyStateMessage?: string;
  noDataMessage?: string;
  noResultsMessage?: string;
  
  // Actions
  actions?: {
    refresh?: () => void;
    export?: () => void;
    bulkDelete?: (selectedIds: string[]) => void;
    customActions?: Array<{
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
      disabled?: boolean;
    }>;
  };
  
  // Styling
  tableAriaLabel?: string;
  className?: string;
  size?: 'small' | 'medium';
  striped?: boolean;
  dense?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string | number;
  
  // Advanced features
  virtualized?: boolean;
  expandable?: boolean;
  renderExpandedRow?: (item: T) => React.ReactNode;
  expandedRows?: string[];
  onExpandedRowsChange?: (expandedIds: string[]) => void;
}

// Loading skeleton for table rows
const TableRowSkeleton: React.FC<{ columns: ColumnDefinition[] }> = ({ columns }) => (
  <TableRow>
    {columns.map((column, index) => (
      <TableCell key={column.key} align={column.align || 'left'}>
        <Skeleton variant="text" width={index === 0 ? '60%' : '80%'} />
      </TableCell>
    ))}
  </TableRow>
);

// Mobile Sort Component
const MobileSortControls: React.FC<{
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  sortOptions: { key: string; label: string }[];
}> = ({ sortConfig, onSort, sortOptions }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel>Sort by</InputLabel>
        <Select
          value={sortConfig.key}
          label="Sort by"
          onChange={(e) => onSort(e.target.value)}
          startAdornment={<SortIcon sx={{ mr: 1 }} />}
        >
          {sortOptions.map(option => (
            <MenuItem key={option.key} value={option.key}>
              {option.label} {sortConfig.key === option.key && (
                sortConfig.direction === 'asc' ? '↑' : '↓'
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

// Table toolbar with actions
const DataTableToolbar: React.FC<{
  title?: string;
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  actions?: DataTableProps['actions'];
  selectedCount?: number;
  selectedItems?: string[];
}> = ({ 
  title, 
  searchable, 
  searchValue, 
  onSearchChange, 
  searchPlaceholder,
  actions,
  selectedCount = 0,
  selectedItems = []
}) => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(selectedCount > 0 && {
          bgcolor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.primary.light
              : theme.palette.primary.dark,
        }),
      }}
    >
      {selectedCount > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {selectedCount} selected
        </Typography>
      ) : (
        <>
          {title && (
            <Typography
              sx={{ flex: '1 1 100%' }}
              variant="h6"
              id="tableTitle"
              component="div"
            >
              {title}
            </Typography>
          )}
          
          {searchable && (
            <Box sx={{ flex: '1 1 100%', maxWidth: 300, ml: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={searchPlaceholder || 'Search...'}
                value={searchValue || ''}
                onChange={(e) => onSearchChange?.(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Action buttons */}
      <Stack direction="row" spacing={1}>
        {selectedCount > 0 && actions?.bulkDelete && (
          <Tooltip title="Delete selected">
            <IconButton onClick={() => actions.bulkDelete!(selectedItems)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
        
        {actions?.refresh && (
          <Tooltip title="Refresh">
            <IconButton onClick={actions.refresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
        
        {actions?.export && (
          <Tooltip title="Export">
            <IconButton onClick={actions.export}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        )}
        
        {actions?.customActions?.map((action, index) => (
          <Tooltip key={index} title={action.label}>
            <span>
              <IconButton 
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon || <MoreVertIcon />}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Stack>
    </Toolbar>
  );
};

/**
 * Enhanced DataTable component for WaveScoop
 * Provides comprehensive table functionality with mobile support, searching, filtering, and more
 */
function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  error = null,
  sortConfig,
  onSort,
  defaultSort,
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  getItemId = (item: T) => item.id,
  onRowClick,
  onRowDoubleClick,
  rowProps,
  page = 0,
  rowsPerPage = 10,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  showPagination = true,
  searchable = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  filters = {},
  onFilterChange,
  renderMobileCard,
  mobileSortOptions = [],
  mobileBreakpoint = 'md',
  emptyStateComponent,
  emptyStateMessage,
  noDataMessage = 'No data available',
  noResultsMessage = 'No results found',
  actions,
  tableAriaLabel = 'data table',
  className,
  size = 'medium',
  striped = false,
  dense = false,
  stickyHeader = false,
  maxHeight,
  virtualized = false,
  expandable = false,
  renderExpandedRow,
  expandedRows = [],
  onExpandedRowsChange,
}: DataTableProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  
  // Internal state
  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig>(
    sortConfig || defaultSort || { key: '', direction: 'asc' }
  );

  // Use provided sort config or internal state
  const currentSortConfig = sortConfig || internalSortConfig;
  
  // Handle sorting
  const handleSort = useCallback((key: string) => {
    const newDirection: 'asc' | 'desc' = currentSortConfig.key === key && currentSortConfig.direction === 'asc' 
      ? 'desc' 
      : 'asc';
    
    const newSortConfig = { key, direction: newDirection };
    
    if (onSort) {
      onSort(key);
    } else {
      setInternalSortConfig(newSortConfig);
    }
    
    log.user.action('table-sort', { key, direction: newDirection });
  }, [currentSortConfig, onSort]);

  // Handle selection
  const handleSelectAll = useCallback((checked: boolean) => {
    if (!onSelectionChange) return;
    
    const newSelection = checked ? data.map(getItemId) : [];
    onSelectionChange(newSelection);
    log.user.action('table-select-all', { checked, count: newSelection.length });
  }, [data, getItemId, onSelectionChange]);

  const handleSelectItem = useCallback((itemId: string, checked: boolean) => {
    if (!onSelectionChange) return;
    
    const newSelection = checked
      ? [...selectedItems, itemId]
      : selectedItems.filter(id => id !== itemId);
    
    onSelectionChange(newSelection);
    log.user.action('table-select-item', { itemId, checked });
  }, [selectedItems, onSelectionChange]);

  // Calculate derived values
  const effectiveTotalCount = totalCount ?? data.length;
  const isAllSelected = data.length > 0 && selectedItems.length === data.length;
  const isSomeSelected = selectedItems.length > 0 && selectedItems.length < data.length;

  // Show error state
  if (error) {
    return (
      <Box className={className}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Show empty state
  const isEmpty = !loading && data.length === 0 && effectiveTotalCount === 0;
  const noResults = !loading && data.length === 0 && effectiveTotalCount > 0;
  
  if (isEmpty && emptyStateComponent) {
    return (
      <Box className={className}>
        {emptyStateComponent}
      </Box>
    );
  }

  if (isEmpty || noResults) {
    const message = noResults ? noResultsMessage : (emptyStateMessage || noDataMessage);
    return (
      <Box className={className}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {message}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Mobile view
  if (isMobile) {
    return (
      <Box className={className}>
        {/* Mobile toolbar */}
        {(searchable || actions) && (
          <DataTableToolbar
            searchable={searchable}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder={searchPlaceholder}
            actions={actions}
            selectedCount={selectedItems.length}
            selectedItems={selectedItems}
          />
        )}

        {/* Mobile Sort Controls */}
        {mobileSortOptions.length > 0 && (
          <MobileSortControls 
            sortConfig={currentSortConfig}
            onSort={handleSort}
            sortOptions={mobileSortOptions}
          />
        )}

        {/* Mobile Cards */}
        <Box>
          {loading && (
            Array.from({ length: rowsPerPage }, (_, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Skeleton variant="rectangular" height={120} />
              </Box>
            ))
          )}
          
          {!loading && data.map((item, index) => {
            const itemId = getItemId(item);
            return (
              <Box 
                key={itemId} 
                onClick={() => onRowClick?.(item, index)}
                onDoubleClick={() => onRowDoubleClick?.(item, index)}
                sx={{ 
                  cursor: onRowClick ? 'pointer' : 'default',
                  mb: 1,
                }}
              >
                {renderMobileCard ? renderMobileCard(item, index) : (
                  <Paper sx={{ p: 2 }}>
                    <Typography>Mobile card renderer not provided</Typography>
                  </Paper>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Pagination */}
        {showPagination && (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={effectiveTotalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange || (() => {})}
            onRowsPerPageChange={onRowsPerPageChange || (() => {})}
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
              },
              '& .MuiTablePagination-spacer': {
                display: { xs: 'none', sm: 'flex' }
              }
            }}
          />
        )}
      </Box>
    );
  }

  // Desktop table view
  return (
    <Box className={className}>
      {/* Desktop toolbar */}
      {(searchable || actions || selectable) && (
        <DataTableToolbar
          searchable={searchable}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          actions={actions}
          selectedCount={selectedItems.length}
          selectedItems={selectedItems}
        />
      )}

      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 1, 
          border: '1px solid', 
          borderColor: 'divider',
          maxHeight: maxHeight
        }}
      >
        <Table 
          stickyHeader={stickyHeader} 
          aria-label={tableAriaLabel}
          size={size}
          sx={{
            ...(striped && {
              '& tbody tr:nth-of-type(odd)': {
                backgroundColor: theme.palette.action.hover,
              },
            }),
          }}
        >
          <TableHead>
            <TableRow>
              {/* Selection checkbox column */}
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
              )}
              
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || 'left'}
                  onClick={column.sortable !== false ? () => handleSort(column.sortKey || column.key) : undefined}
                  sx={{ 
                    cursor: column.sortable !== false ? 'pointer' : 'default',
                    width: column.width,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    ...(column.sticky && { position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }),
                    ...column.headerProps,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {column.headerRender ? column.headerRender() : column.label}
                    {column.sortable !== false && currentSortConfig.key === (column.sortKey || column.key) && (
                      currentSortConfig.direction === 'asc' ? 
                        <ArrowUpIcon fontSize="small" /> : 
                        <ArrowDownIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              Array.from({ length: rowsPerPage }, (_, index) => (
                <TableRowSkeleton key={index} columns={columns} />
              ))
            )}
            
            {!loading && data.map((item, index) => {
              const itemId = getItemId(item);
              const isSelected = selectedItems.includes(itemId);
              const isExpanded = expandedRows.includes(itemId);
              
              return (
                <React.Fragment key={itemId}>
                  <TableRow 
                    hover
                    selected={isSelected}
                    sx={{ 
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                      },
                    }}
                    onClick={() => onRowClick?.(item, index)}
                    onDoubleClick={() => onRowDoubleClick?.(item, index)}
                    {...(rowProps?.(item, index) || {})}
                  >
                    {/* Selection checkbox */}
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectItem(itemId, e.target.checked);
                          }}
                        />
                      </TableCell>
                    )}
                    
                    {columns.map((column, columnIndex) => (
                      <TableCell 
                        key={column.key}
                        align={column.align || 'left'}
                        component={columnIndex === 0 ? "th" : "td"}
                        scope={columnIndex === 0 ? "row" : undefined}
                        sx={{
                          ...(column.sticky && { position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }),
                        }}
                        {...(column.cellProps?.(item) || {})}
                      >
                        {column.render ? column.render(item, index) : (
                          <Typography variant="body2">
                            {String((item as any)[column.key] || '')}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  
                  {/* Expanded row */}
                  {expandable && isExpanded && renderExpandedRow && (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + (selectable ? 1 : 0)} 
                        sx={{ py: 0 }}
                      >
                        <Box sx={{ py: 2 }}>
                          {renderExpandedRow(item)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={effectiveTotalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange || (() => {})}
          onRowsPerPageChange={onRowsPerPageChange || (() => {})}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'flex' }
            }
          }}
        />
      )}
    </Box>
  );
}

export default DataTable;